import React, { useMemo } from 'react';

import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage } from '@futrli/localisation';
import { ListingPageRowActionItem } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-actions/listing-page-row-actions';
import {
  ExtendedOrganisationListItem,
  ExtendedWorkspaceListItem
} from '@futrli/views/operations/workspaces/processors/workspacesList';

import { useOrganisationsActions } from '../../context/organisations-actions-context/organisations-actions-context';

interface UseOrganisationActionItemsParams {
  workspace: ExtendedWorkspaceListItem;
  organisation: ExtendedOrganisationListItem;
}

export const useOrganisationActionItems = ({
  workspace,
  organisation
}: UseOrganisationActionItemsParams): ListingPageRowActionItem[] => {
  const {
    handleDeleteOrganisationClick,
    handleReconnectOrganisationClick,
    handleRemoveAccessClick,
    handleResubscribeOrganisationClick,
    handleUpdateValuesClick
  } = useOrganisationsActions();

  return useMemo<ListingPageRowActionItem[]>(() => {
    const items: ListingPageRowActionItem[] = [];
    const { actions } = organisation;

    if (actions.reconnect) {
      items.push({
        label: <FormattedMessage id="common.reconnect" defaultMessage="Reconnect" />,
        onClick: () => handleReconnectOrganisationClick(workspace, organisation)
      });
    }

    if (actions.resubscribe) {
      items.push({
        label: <FormattedMessage id="datasource.resubscribe" defaultMessage="Resubscribe" />,
        onClick: () => handleResubscribeOrganisationClick(organisation)
      });
    }

    if (actions.updateValues) {
      items.push({
        label: <FormattedMessage id="predict.update_data" defaultMessage="Update data" />,
        onClick: () => handleUpdateValuesClick(workspace, organisation)
      });
    }

    if (actions.removeAccess) {
      items.push({
        label: <FormattedMessage id="common.remove_access" defaultMessage="Remove access" />,
        onClick: () => handleRemoveAccessClick(workspace, organisation)
      });
    }

    if (actions.delete) {
      items.push({
        label: (
          <>
            <Icon type="delete" fontSize="small" />
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </>
        ),
        onClick: () => handleDeleteOrganisationClick(workspace, organisation),
        variant: 'danger'
      });
    }

    return items;
  }, [
    handleDeleteOrganisationClick,
    handleReconnectOrganisationClick,
    handleRemoveAccessClick,
    handleResubscribeOrganisationClick,
    handleUpdateValuesClick,
    organisation,
    workspace
  ]);
};
